import { COUNTRIES } from "../../../../constants/countries";
import { AddBranchDTO, BranchBasicInfo, BranchSettings } from "../../../../services/dto/request/branches-request-dto";

interface BuildParamsOptions {
  countryCode: string;
  branchBasicInfo: BranchBasicInfo;
  branchSettings: BranchSettings;
  withSplitTip: boolean;
  updatedReviewLink: string | null;
  withTipAccount: boolean;
  isDraft: boolean;
  status: number;
}

/**
 * Construye los parámetros base para crear/editar una sucursal según el país
 * Cada país puede tener campos obligatorios u opcionales diferentes
 */
export const buildBranchParamsByCountry = (
  options: BuildParamsOptions
): Omit<AddBranchDTO, 'IsInCharge' | 'managerName' | 'managerLastName' | 'managerDocumentType' | 'managerDocumentNumber' | 'managerPhone' | 'managerAddress' | 'managerProvinceId' | 'managerCityId' | 'managerDniFront' | 'managerDniBack' | 'managerSelfie'> => {
  const {
    countryCode,
    branchBasicInfo,
    branchSettings,
    withSplitTip,
    updatedReviewLink,
    withTipAccount,
    isDraft,
    status,
  } = options;

  // Campos comunes a todos los países (sin activity fields)
  const commonParams = {
    Address: branchBasicInfo.address,
    Name: branchBasicInfo.name,
    FantasyName: branchBasicInfo.name,
    Phone: branchBasicInfo.phone,
    IsSameAddress: branchBasicInfo.sameAddress,
    EqualSplitTip: withSplitTip ? branchSettings.equalSplitTip : false,
    GoogleLink: updatedReviewLink ?? "",
    OnlyRatings: branchSettings.onlyRatings,
    Status: status,
    withTipAccount: withTipAccount,
    draftMode: isDraft,
  };

  switch (countryCode) {
    case COUNTRIES.DO: // República Dominicana
      return {
        ...commonParams,
        withTipAccount: false, // RD no requiere cuenta de propinas
        // RD no requiere campos de dirección de actividad
        ActivityAddress: "",
        ActivityFloor: null,
        ActivityPostalCode: "",
        ActivityDepartment: null,
        ActivityProvinceId: 0,
        ActivityCityId: 0,
        
        // Campos específicos de RD
        IsPhysicalPerson: false,
        TaxPayer: false,
        IdentityType: "CUIT" as any, // Valor por defecto (no se usa en RD)
        IdentityNumber: "", // RD no requiere CUIT/CUIL al crear sucursal
        
        // Campos de dirección principal tampoco requeridos
        Floor: null,
        PostalCode: "",
        Department: null,
        ProvinceId: 0,
        CityId: 0,
        afipFile: null as any, // RD no usa AFIP
      };

    case COUNTRIES.AR: // Argentina
    default:
      return {
        ...commonParams,
        // Campos específicos de Argentina
        IsPhysicalPerson: branchSettings.personType === "PHYSICAL",
        TaxPayer: branchSettings.personType === "PHYSICAL" ? branchSettings.isTaxPayer : false,
        IdentityType: branchSettings.identityType,
        IdentityNumber: branchSettings.identityNumber, // Argentina SÍ requiere CUIT/CUIL
        
        // Campos de dirección de actividad (AR sí los usa)
        ActivityAddress: branchBasicInfo.sameAddress ? branchBasicInfo.address : branchBasicInfo.activityAddress || "",
        ActivityFloor: branchBasicInfo.sameAddress ? branchBasicInfo.floor : branchBasicInfo.activityFloor || null,
        ActivityPostalCode: branchBasicInfo.sameAddress ? branchBasicInfo.postalCode : branchBasicInfo.activityPostalCode || "",
        ActivityDepartment: branchBasicInfo.sameAddress ? branchBasicInfo.department : branchBasicInfo.activityDepartment || null,
        ActivityProvinceId: branchBasicInfo.sameAddress ? branchBasicInfo.branchProvinceId : branchBasicInfo.activityProvinceId || 0,
        ActivityCityId: branchBasicInfo.sameAddress ? branchBasicInfo.branchCityId : branchBasicInfo.activityCityId || 0,
        Floor: branchBasicInfo.floor || null,
        PostalCode: branchBasicInfo.postalCode,
        Department: branchBasicInfo.department || null,
        ProvinceId: branchBasicInfo.branchProvinceId,
        CityId: branchBasicInfo.branchCityId,
        afipFile: branchBasicInfo.afipFile!,
        BirthDate: branchBasicInfo.birthDate,
        Occupation: branchBasicInfo.occupation,
        IvaCondition: branchBasicInfo.ivaCondition,
        IsPEP: branchBasicInfo.isPEP,
        PepPosition: branchBasicInfo.isPEP ? branchBasicInfo.pepPosition : null,
        IsUIF: branchBasicInfo.isUIF,
        Gender: branchBasicInfo.gender,
        CivilStatus: branchBasicInfo.civilStatus,
      };
  }
};

